import * as React from 'react';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { Button } from './button/button';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';

interface ConfirmPopoverProps {
  children: React.ReactNode;
  onConfirm: () => void;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link' | 'brand' | 'success';
  disabled?: boolean;
  isLoading?: boolean;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
}

const ConfirmPopover: React.FC<ConfirmPopoverProps> = ({
  children,
  onConfirm,
  title,
  confirmText,
  cancelText,
  confirmVariant = 'success',
  disabled = false,
  isLoading = false,
  side = 'top',
  align = 'center',
}) => {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const wasLoadingRef = React.useRef(false);

  const handleConfirm = () => {
    onConfirm();
    // Don't close immediately - popover stays open until mutation completes
  };

  // Close popover when isLoading transitions from true to false (mutation completed)
  React.useEffect(() => {
    if (wasLoadingRef.current && !isLoading && open) {
      // Mutation completed - close the popover
      setOpen(false);
    }
    wasLoadingRef.current = isLoading;
  }, [isLoading, open]);

  const handleCancel = () => {
    setOpen(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(newOpen) => {
        // Prevent closing while loading
        if (!newOpen && isLoading) return;
        setOpen(newOpen);
      }}
    >
      <PopoverTrigger asChild disabled={disabled || isLoading}>
        {children}
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="tw-w-auto tw-min-w-[200px] tw-max-w-[280px] tw-p-3"
      >
        <div className="tw-flex tw-flex-col tw-gap-3">
          <p className="tw-text-sm tw-font-medium tw-text-foreground">
            {title || t('confirm.default_title', { defaultValue: 'Are you sure?' })}
          </p>
          <div className="tw-flex tw-justify-end tw-gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              disabled={isLoading}
              className="tw-h-8 tw-px-3"
            >
              {cancelText || t('common:cancel', { defaultValue: 'Cancel' })}
            </Button>
            <Button
              variant={confirmVariant}
              size="sm"
              onClick={handleConfirm}
              disabled={isLoading}
              className="tw-h-8 tw-px-3"
            >
              {isLoading && <Loader2 className="tw-mr-1 tw-h-3 tw-w-3 tw-animate-spin" />}
              {confirmText || t('common:confirm', { defaultValue: 'Confirm' })}
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { ConfirmPopover };
